import { createClient } from "@supabase/supabase-js";
import type { Database } from "../types/supabase";

const supabaseUrl = 'https://supamozo.live'
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJzdXBhYmFzZSIsImlhdCI6MTc0ODk2OTgyMCwiZXhwIjo0OTA0NjQzNDIwLCJyb2xlIjoiYW5vbiJ9.KUJJyT2uuuiLvv9FQNBoabi5DNKGWYoi5Hfo8KDddI0'
export const supabase = createClient<Database>(supabaseUrl, supabaseKey)

// Service worker registration
const isServiceWorkerSupported = "serviceWorker" in navigator;
const isDevEnvironment = import.meta.env.DEV;

// Auto-update state management
let updateAvailable = false;
let newWorkerWaiting: ServiceWorker | null = null;

// Helper function to show update notification
const showUpdateNotification = () => {
  // Create a subtle notification that doesn't interrupt user experience
  const notification = document.createElement('div');
  notification.id = 'app-update-notification';
  notification.innerHTML = `
    <div style="
      position: fixed;
      top: 20px;
      right: 20px;
      background: #065900;
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 14px;
      max-width: 300px;
      animation: slideIn 0.3s ease-out;
    ">
      <div style="display: flex; align-items: center; gap: 8px;">
        <div style="width: 8px; height: 8px; background: #4ade80; border-radius: 50%; animation: pulse 2s infinite;"></div>
        <span>New version available! Updating automatically...</span>
      </div>
    </div>
    <style>
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
    </style>
  `;
  
  document.body.appendChild(notification);
  
  // Auto-remove notification after 3 seconds
  setTimeout(() => {
    if (notification.parentNode) {
      notification.remove();
    }
  }, 3000);
};

// Helper function to apply update
const applyUpdate = () => {
  if (newWorkerWaiting) {
    newWorkerWaiting.postMessage({ type: 'SKIP_WAITING' });
  }
};

// Helper function to register service worker with auto-update
const registerServiceWorker = async () => {
  try {
    const registration = await navigator.serviceWorker.register("/sw.js", {
      scope: "/",
      updateViaCache: "none", // Disable caching of the service worker file
    });

    // Immediately check for updates
    registration.update();

    // Handle updates
    registration.addEventListener("updatefound", () => {
      const newWorker = registration.installing;
      if (!newWorker) return;

      newWorker.addEventListener("statechange", () => {
        if (
          newWorker.state === "installed" &&
          navigator.serviceWorker.controller
        ) {
          // New content is available
          updateAvailable = true;
          newWorkerWaiting = newWorker;
          
          if (isDevEnvironment) {
            console.log(" New app version available! Will auto-update on next navigation.");
          }
          
          // Show subtle notification
          showUpdateNotification();
          
          // Auto-apply update after a short delay to allow user to see notification
          setTimeout(() => {
            applyUpdate();
          }, 2000);
        }
      });
    });

    // Listen for the controlling service worker changing and reload the page
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (updateAvailable) {
        if (isDevEnvironment) {
          console.log(" App updated! Reloading page...");
        }
        window.location.reload();
      }
    });

    // Check for updates periodically (every 5 minutes)
    setInterval(() => {
      registration.update();
    }, 5 * 60 * 1000);

    // Check for updates when page becomes visible (user switches back to tab)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        registration.update();
      }
    });

    // Check for updates on focus (user clicks on window)
    window.addEventListener('focus', () => {
      registration.update();
    });

    if (isDevEnvironment) {
      console.log(" Service Worker registered with auto-update functionality");
    }
  } catch (error) {
    if (isDevEnvironment) {
      console.error(" Service Worker registration failed:", error);
    }
  }
};

const isUnsupportedEnvironment =
  window.navigator.userAgent.includes("StackBlitz") || // Skip in StackBlitz
  (window.location.protocol !== "https:" &&
    !["localhost", "127.0.0.1"].includes(window.location.hostname)); // Require HTTPS except on localhost

if (isServiceWorkerSupported && !isUnsupportedEnvironment) {
  window.addEventListener("load", () => {
    registerServiceWorker().catch((error) => {
      if (isDevEnvironment) {
        console.warn("Failed to setup Service Worker:", error);
      }
    });
  });
} else if (isDevEnvironment) {
  // Provide helpful debugging information in development
  if (!isServiceWorkerSupported) {
    console.info("Service Workers are not supported in this browser");
  } else if (isUnsupportedEnvironment) {
    console.info("Service Worker registration skipped in this environment");
  }
}
